import React, { useState, useEffect } from 'react';
import axios from 'axios';
import moment from 'moment';
import { FaTools, FaExclamationTriangle, FaCalendarAlt, FaTachometerAlt } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import VehicleDetailsModal from '../VehicleManagement/VehicleDetailsModal';
import { cars, handoverService } from '../../services/api';

// Safe wrapper for VehicleDetailsModal that handles potential API errors
const SafeVehicleDetailsModal = ({ vehicle, car, isOpen, onClose, onUpdate }) => {
  const [error, setError] = useState(null);
  const [localVehicle, setLocalVehicle] = useState(null);
  const [localCar, setLocalCar] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const token = localStorage.getItem('adminToken') || localStorage.getItem('userToken');

  // Reset error and prepare data when modal is opened
  useEffect(() => {
    if (isOpen && vehicle && vehicle._id) {
      setError(null);
      setIsLoading(true);

      const fetchCarAndVehicleData = async () => {
        try {
          // Get the car ID
          const carId = vehicle.carId || vehicle.carModelId;

          if (carId) {
            try {
              // Fetch complete car data
              const response = await axios.get(`https://rent-server.create-site.gr/api/cars/${carId}`, {
                headers: { 'Authorization': `Bearer ${token}` }
              });

              if (response.data) {
                // Find the specific vehicle instance
                const vehicleInstance = response.data.vehicleInstances?.find(v => v._id === vehicle._id);

                if (vehicleInstance) {
                  // Use the vehicle instance from the car data
                  setLocalVehicle(vehicleInstance);
                  setLocalCar(response.data);
                } else {
                  // If vehicle not found in car data, create a safe vehicle object
                  setLocalVehicle({
                    ...vehicle,
                    _id: vehicle._id,
                    carId: carId,
                    licensePlate: vehicle.licensePlate || 'Unknown',
                    status: vehicle.status || 'unknown',
                    currentMileage: vehicle.currentMileage || 0,
                    lastServiceDate: vehicle.lastServiceDate || null
                  });
                  setLocalCar(response.data);
                }
              } else {
                throw new Error('No car data returned');
              }
            } catch (err) {
              console.error('Error fetching detailed car data:', err);
              // Fallback to basic data
              createFallbackData();
            }
          } else {
            // No car ID available, use fallback data
            createFallbackData();
          }
        } catch (err) {
          console.error('Error preparing data for vehicle modal:', err);
          setError("There was an error preparing the vehicle data.");
          createFallbackData();
        } finally {
          setIsLoading(false);
        }
      };

      const createFallbackData = () => {
        // Ensure we have a proper car ID
        const carId = vehicle.carId || vehicle.carModelId || `car_model_${vehicle._id}`;
        
        // Create a simplified version of the vehicle data with essential fields
        const safeVehicle = {
          ...vehicle,
          _id: vehicle._id || 'unknown-id',
          carId: carId,
          carModelId: carId,
          licensePlate: vehicle.licensePlate || 'Unknown',
          status: vehicle.status || 'unknown',
          currentMileage: vehicle.currentMileage || 0,
          lastServiceDate: vehicle.lastServiceDate || null,
          carModel: vehicle.carModel || 'Unknown Model',
          carMake: vehicle.carMake || 'Unknown Make',
          model: vehicle.model || vehicle.carModel || 'Unknown Model', // For compatibility
          make: vehicle.make || vehicle.carMake || 'Unknown Make',     // For compatibility
          // Add a serviceHistory array to prevent null checks in the modal
          serviceHistory: [],
          // Add insurance data to prevent null checks
          insurance: vehicle.insurance || {
            provider: 'Unknown',
            policyNumber: 'N/A',
            startDate: new Date(),
            endDate: new Date(new Date().setFullYear(new Date().getFullYear() + 1)),
            type: 'basic',
            coverageAmount: 0
          }
        };
        
        // Create car model data with proper structure for API calls
        const safeCar = {
          _id: carId,
          id: carId, // Some components might expect this format
          stringId: carId.toString(), // For string-based IDs
          make: vehicle.carMake || vehicle.make || 'Unknown Make',
          model: vehicle.carModel || vehicle.model || 'Unknown Model',
          year: vehicle.year || new Date().getFullYear(),
          // Add important fields that may be accessed during service record operations
          vehicleInstances: [{
            _id: vehicle._id,
            id: vehicle._id, // Some components might expect this format
            licensePlate: vehicle.licensePlate || 'Unknown',
            carModel: vehicle.carModel || vehicle.model || 'Unknown Model',
            carMake: vehicle.carMake || vehicle.make || 'Unknown Make'
          }]
        };
        
        setLocalVehicle(safeVehicle);
        setLocalCar(safeCar);
      };

      fetchCarAndVehicleData();
    }
  }, [isOpen, vehicle, token]);

  // Show error message if needed
  if (error) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full">
          <h3 className="text-lg font-medium text-red-600 mb-3">Error Loading Data</h3>
          <p className="mb-4">{error}</p>
          <div className="flex justify-end">
            <button
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }
  // Show loading indicator
  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full flex flex-col items-center">
          <div className="w-12 h-12 border-4 border-gray-200 border-t-blue-600 rounded-full animate-spin mb-4"></div>
          <p className="text-gray-700">Loading vehicle details...</p>
        </div>
      </div>
    );
  }

  // Don't render if we don't have processed data yet
  if (!localVehicle || !localCar) {
    return null;
  }
  return (
    <VehicleDetailsModal 
      vehicle={localVehicle}
      car={localCar}
      isOpen={isOpen} 
      onClose={onClose} 
      onUpdate={onUpdate}
      // Override the API services with our mocks when needed
      carsAPI={cars}
      handoverService={handoverService}
    />
  );
};

const MaintenanceNotification = ({ token }) => {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchVehiclesNeedingMaintenance();
  }, []);

  const fetchVehiclesNeedingMaintenance = async () => {
    setLoading(true);
    try {
      const response = await axios.get('https://rent-server.create-site.gr/api/cars/maintenance/upcoming', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setVehicles(response.data || []);
    } catch (err) {
      console.error('Error fetching maintenance data:', err);
      setError('Could not load maintenance data');
    } finally {
      setLoading(false);
    }
  };

  // Determine urgency level based on days since last service or mileage
  const getUrgencyLevel = (vehicle) => {
    if (!vehicle.lastServiceDate) return 'high';
    
    const daysSinceService = moment().diff(moment(vehicle.lastServiceDate), 'days');
    
    if (daysSinceService > 300) return 'critical';
    if (daysSinceService > 180) return 'high';
    if (daysSinceService > 150) return 'medium';
    return 'low';
  };

  // Get color class based on urgency level
  const getUrgencyColorClass = (urgency) => {
    switch (urgency) {
      case 'critical':
        return 'text-red-700 bg-red-50 border-red-200';
      case 'high':
        return 'text-orange-700 bg-orange-50 border-orange-200';
      case 'medium':
        return 'text-yellow-700 bg-yellow-50 border-yellow-200';
      default:
        return 'text-blue-700 bg-blue-50 border-blue-200';
    }
  };

  // Get icon based on urgency level
  const getUrgencyIcon = (urgency) => {
    switch (urgency) {
      case 'critical':
      case 'high':
        return <FaExclamationTriangle className="text-red-500" />;
      case 'medium':
        return <FaCalendarAlt className="text-yellow-500" />;
      default:
        return <FaTools className="text-blue-500" />;
    }
  };

  // Count vehicles by urgency
  const getUrgencyCounts = () => {
    const counts = { critical: 0, high: 0, medium: 0, low: 0 };
    
    vehicles.forEach(vehicle => {
      counts[getUrgencyLevel(vehicle)]++;
    });
    
    return counts;
  };

  const counts = getUrgencyCounts();
  const totalMaintenanceNeeded = vehicles.length;  // Handler for clicking on a vehicle card to open modal
  const handleVehicleClick = async (vehicle) => {
    try {
      if (!vehicle || !vehicle._id) {
        console.error('Invalid vehicle data:', vehicle);
        return;
      }

      setLoading(true);
      
      // Extract or create a carId - this is crucial for API calls
      const carId = vehicle.carId || vehicle.carModelId;
      
      if (carId) {
        try {
          // Fetch detailed car model information
          const response = await axios.get(`https://rent-server.create-site.gr/api/cars/${carId}`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          if (response.data) {
            // Find the specific vehicle instance in the car's vehicleInstances array
            const vehicleInstance = response.data.vehicleInstances?.find(v => v._id === vehicle._id);
            
            if (vehicleInstance) {
              // We found the specific vehicle in the car's data - use that with all fields
              // Add car reference to the vehicle instance to ensure proper operation
              const enhancedVehicle = {
                ...vehicleInstance,
                carId: response.data._id,  // Ensure carId is accessible
                car: response.data         // Add direct reference to car
              };
              
              setSelectedVehicle(enhancedVehicle);
            } else {
              // Vehicle not found in car data, create a hybrid object
              const enhancedVehicle = {
                ...vehicle,
                _id: vehicle._id,
                carId: response.data._id,
                carModelId: response.data._id,  // Add for compatibility
                carMake: response.data.make,
                carModel: response.data.model,
                car: response.data              // Add direct reference to car
              };
              
              setSelectedVehicle(enhancedVehicle);
            }
            
            setIsModalOpen(true);
          } else {
            throw new Error('No car data returned');
          }
        } catch (err) {
          console.error('Error fetching car data:', err);
          // Fallback to using the vehicle data directly with some enhancements
          setSelectedVehicle({
            ...vehicle,
            carId: carId,
            carModelId: carId
          });
          setIsModalOpen(true);
        }
      } else {
        // No car ID available, construct data from what we have
        const reconstructedCarId = `car_${vehicle._id}`;
        
        const enhancedVehicle = {
          ...vehicle,
          carId: reconstructedCarId,
          carModelId: reconstructedCarId,
          // Ensure these fields exist
          carMake: vehicle.carMake || vehicle.make || 'Unknown Make',
          carModel: vehicle.carModel || vehicle.model || 'Unknown Model'
        };
        
        setSelectedVehicle(enhancedVehicle);
        setIsModalOpen(true);
      }
    } catch (err) {
      console.error('Error preparing vehicle data for modal:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="relative">
        <button className="relative p-2 text-gray-600 hover:text-gray-800">
          <FaTools className="w-6 h-6" />
          <div className="absolute top-1 right-1 w-3 h-3 rounded-full bg-gray-300 animate-pulse"></div>
        </button>
      </div>
    );
  }

  if (error) {
    return (
      <div className="relative">
        <button className="relative p-2 text-red-600 hover:text-red-800">
          <FaTools className="w-6 h-6" />
          <div className="absolute top-1 right-1 w-3 h-3 rounded-full bg-red-500"></div>
        </button>
      </div>
    );
  }
  
  if (totalMaintenanceNeeded === 0) {
    return (
      <div className="relative">
        <button className="relative p-2 text-green-600 hover:text-green-800">
          <FaTools className="w-6 h-6" />
        </button>
      </div>
    );
  }

  return (
    <div className="relative">
      <button 
        className="relative p-2 text-gray-600 hover:text-gray-800 focus:outline-none"
        onClick={() => setIsOpen(!isOpen)}
      >
        <FaTools className="w-6 h-6" />
        {totalMaintenanceNeeded > 0 && (
          <div className="absolute top-1 right-1 w-5 h-5 rounded-full bg-yellow-500 text-white text-xs flex items-center justify-center">
            {totalMaintenanceNeeded}
          </div>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 sm:w-96 bg-white rounded-md shadow-lg z-20 border border-gray-200">
          <div className="p-3 border-b border-gray-200 bg-blue-800 text-white rounded-t-md">
            <h3 className="font-semibold flex items-center text-sm sm:text-base">
              <FaTools className="mr-2" /> Maintenance Alerts
            </h3>
          </div>

          <div className="p-3 border-b border-gray-200 bg-gray-100">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
              <div className="text-center p-2 bg-red-50 rounded border border-red-200">
                <div className="font-bold text-red-700">{counts.critical}</div>
                <div>Critical</div>
              </div>
              <div className="text-center p-2 bg-orange-50 rounded border border-orange-200">
                <div className="font-bold text-orange-700">{counts.high}</div>
                <div>High</div>
              </div>
              <div className="text-center p-2 bg-yellow-50 rounded border border-yellow-200">
                <div className="font-bold text-yellow-700">{counts.medium}</div>
                <div>Medium</div>
              </div>
              <div className="text-center p-2 bg-blue-50 rounded border border-blue-200">
                <div className="font-bold text-blue-700">{counts.low}</div>
                <div>Low</div>
              </div>
            </div>
          </div>

          <div className="max-h-96 overflow-y-auto">
            {vehicles.length > 0 ? (
              <div className="p-3">
                {vehicles.map(vehicle => {
                  const urgency = getUrgencyLevel(vehicle);
                  const colorClass = getUrgencyColorClass(urgency);
                  const icon = getUrgencyIcon(urgency);
                  
                  return (
                    <div 
                      key={vehicle._id}
                      className={`mb-3 p-3 rounded border ${colorClass} cursor-pointer hover:shadow-md transition-shadow duration-200`}
                      onClick={() => handleVehicleClick(vehicle)}
                    >
                      <div className="flex flex-col sm:flex-row justify-between items-start">
                        <div className="mb-2 sm:mb-0">
                          <div className="font-medium text-sm sm:text-base">
                            {vehicle.carMake} {vehicle.carModel}
                          </div>
                          <div className="text-xs sm:text-sm text-gray-700">
                            {vehicle.licensePlate}
                          </div>
                        </div>
                        <div className="flex flex-col items-start sm:items-end w-full sm:w-auto">
                          <div className="flex items-center mb-1">
                            {icon}
                            <span className="ml-1 text-xs sm:text-sm font-medium">
                              {urgency.charAt(0).toUpperCase() + urgency.slice(1)}
                            </span>
                          </div>
                          
                          {vehicle.lastServiceDate ? (
                            <div className="text-xs flex items-center">
                              <FaCalendarAlt className="mr-1" size={10} />
                              Last service: {moment(vehicle.lastServiceDate).format('DD/MM/YY')}
                            </div>
                          ) : (
                            <div className="text-xs text-red-600">No service history</div>
                          )}
                          
                          {vehicle.currentMileage && (
                            <div className="text-xs flex items-center">
                              <FaTachometerAlt className="mr-1" size={10} />
                              {vehicle.currentMileage} km
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="p-6 text-center text-gray-500">
                All vehicles are up to date on maintenance
              </div>
            )}
          </div>

          <div className="p-3 border-t border-gray-200 bg-gray-50 text-right rounded-b-md">
            <button 
              className="text-blue-600 hover:text-blue-800 text-xs sm:text-sm font-medium"
              onClick={() => navigate('/vehicle-service-report')}
            >
              View Full Service Report
            </button>
          </div>
        </div>
      )}      {/* Vehicle Details Modal */}
      {selectedVehicle && (
        <SafeVehicleDetailsModal
          vehicle={selectedVehicle}
          car={selectedVehicle.car || {
            _id: selectedVehicle.carId || `car_${selectedVehicle._id}`,
            id: selectedVehicle.carId || `car_${selectedVehicle._id}`,
            stringId: (selectedVehicle.carId || `car_${selectedVehicle._id}`).toString(),
            make: selectedVehicle.carMake || selectedVehicle.make || 'Unknown',
            model: selectedVehicle.carModel || selectedVehicle.model || 'Unknown',
            year: selectedVehicle.year || new Date().getFullYear(),
            vehicleInstances: [{
              _id: selectedVehicle._id,
              id: selectedVehicle._id,
              licensePlate: selectedVehicle.licensePlate || 'Unknown',
              carMake: selectedVehicle.carMake || selectedVehicle.make || 'Unknown',
              carModel: selectedVehicle.carModel || selectedVehicle.model || 'Unknown',
              status: selectedVehicle.status || 'unknown',
              currentMileage: selectedVehicle.currentMileage || 0
            }]
          }}
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedVehicle(null);
          }}
          onUpdate={() => {
            fetchVehiclesNeedingMaintenance();
          }}
        />
      )}
    </div>
  );
};

export default MaintenanceNotification;
